import { useWindowSize } from '@folklore/hooks';
import { DataProvider } from '@micromag/data';
import '@micromag/intl/locale/en';
import '@micromag/intl/locale/fr';
import Viewer from '@micromag/viewer';
import classNames from 'classnames';
import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'wouter';

import useStoryTrackingVariables from '../../hooks/useStoryTrackingVariables';

import { useSite } from '../../contexts/SiteContext';
import { MicromagItem, MicromagStory } from '../../types/micromag';

import menuLogo from '../../img/laposte-white.png';
import styles from '../../styles/partials/micromag.module.css';

interface MicromagProps {
    slug?: string | null;
    micromag?: MicromagStory | null;
    micromags?: MicromagItem[] | null;
    viewerMenuHeader?: React.ReactNode;
    currentScreenId?: string;
    paused?: boolean;
    fullscreen?: boolean;
    autoFullscreen?: boolean;
    withoutPlaybackControls?: boolean;
    basePath?: string | null;
    withoutRouter?: boolean;
    onClose?: (() => void) | null;
    onEnd?: (() => void) | null;
    onScreenChange?: (...args: any[]) => void;
    onMenuChange?: (...args: any[]) => void;
    hasHome?: boolean;
    className?: string | number | symbol | any;
}

function Micromag({
    slug = null,
    micromag = null,
    micromags = null,
    viewerMenuHeader = null,
    currentScreenId = null,
    paused = false,
    fullscreen: initialFullscreen = false,
    autoFullscreen = false,
    withoutPlaybackControls = false,
    basePath = null,
    withoutRouter = true,
    onClose: customOnClose = null,
    onEnd: customOnEnd = null,
    onScreenChange = null,
    onMenuChange = null,
    hasHome = false,
    className = null,
}: MicromagProps) {
    const googleApiKey = null;
    const micromagApiBaseUrl = 'https://microm.ag/api';

    const { document_id: documentId = null, organisation = null } = micromag || {};
    const { branding = null } = organisation || {};
    const { isKiosk = false } = useSite();
    const { width = 0, height = 0 } = useWindowSize();
    const [landscape, setLandscape] = useState(width > height);
    const [fullscreen, setFullscreen] = useState(autoFullscreen && !landscape && initialFullscreen);

    const onClose = useCallback(() => {
        if (autoFullscreen && fullscreen) {
            setFullscreen(false);
        }
        if (customOnClose !== null) {
            customOnClose();
        }
    }, [autoFullscreen, fullscreen, setFullscreen, customOnClose]);

    const onEnd = useCallback(() => {
        if (autoFullscreen && fullscreen) {
            setFullscreen(false);
        }
        if (customOnEnd !== null) {
            customOnEnd();
        }
    }, [autoFullscreen, fullscreen, setFullscreen, customOnEnd]);

    const onViewModeChange = useCallback(
        ({ landscape: isLandscape = false }) => {
            setLandscape(isLandscape);
            if (autoFullscreen && !isLandscape) {
                setFullscreen(true);
            }
        },
        [autoFullscreen, setLandscape, setFullscreen],
    );

    const trackingVariables = useStoryTrackingVariables(micromag);

    const finalMicromag = useMemo(
        () => ({
            ...micromag,
        }),
        [micromag],
    );

    const isFrench = documentId === '1017' || slug === 'dossier-international';

    const language = isFrench ? 'fr' : 'en';

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.fullscreen]: fullscreen,
                    [className]: className !== null,
                },
            ])}
            lang={language}
        >
            <DataProvider apiBaseUrl={micromagApiBaseUrl}>
                <Viewer
                    story={finalMicromag}
                    trackingVariables={trackingVariables}
                    paused={paused}
                    theme={branding}
                    locale={language}
                    visitor={null}
                    memoryRouter={basePath === null}
                    withoutRouter={withoutRouter}
                    basePath={basePath}
                    screen={currentScreenId}
                    closeable={(autoFullscreen && fullscreen) || customOnClose !== null}
                    googleApiKey={googleApiKey}
                    // backToFirstScreenTimeout={isKiosk ? 30 * 1000 : null}
                    // muted={!isKiosk}
                    onViewModeChange={onViewModeChange}
                    onScreenChange={onScreenChange}
                    onMenuChange={onMenuChange}
                    onClose={onClose}
                    onEnd={onEnd}
                    shareOptions={['linkedin', 'email']}
                    withoutPlaybackControls={withoutPlaybackControls}
                    withNavigationHint
                    withMicromagBranding
                    pathWithIndex
                    menuHeader={viewerMenuHeader}
                    menuItems={
                        !isKiosk
                            ? [
                                  <div className={styles.links}>
                                      <Link
                                          key="languageSwitch"
                                          className={styles.link}
                                          to={'/'}
                                          hrefLang={isFrench ? 'en' : 'fr'}
                                          lang="fr"
                                      >
                                          <img src={menuLogo} alt="" className={styles.menuLogo} />
                                      </Link>
                                      <Link
                                          key="languageSwitch"
                                          className={styles.link}
                                          to={
                                              isFrench
                                                  ? '/international-report'
                                                  : '/dossier-international'
                                          }
                                          hrefLang={isFrench ? 'en' : 'fr'}
                                          lang="fr"
                                      >
                                          {isFrench ? 'English version' : 'Version française'}
                                          <span aria-hidden="true">&nbsp;&rarr;</span>
                                      </Link>
                                  </div>,
                                  'share',
                                  'main',
                              ]
                            : ['main']
                    }
                />
            </DataProvider>
        </div>
    );
}

export default Micromag;
